import { useEffect, useState } from "react";
import { AppSwitcherMenu } from "@/base/NewselaGlobalLayoutV2/NavigationBar/AppSwitcherMenu";
import { HomeMenu } from "@/base/NewselaGlobalLayoutV2/NavigationBar/HomeMenu";
import { useNavigationBarV2Context } from "@/base/NewselaGlobalLayoutV2/NavigationBar/NavigationBar.provider";
import {
  getNavigationBarRightContainerCss,
  getTrialButtonsVisibilityCss,
  navigationBarContainerCss,
  navigationBarLeftContainerCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/NavigationBar.styles";
import { NewselaLogo } from "@/base/NewselaGlobalLayoutV2/NavigationBar/NewselaLogo";
import { ResourceMenus } from "@/base/NewselaGlobalLayoutV2/NavigationBar/ResourceMenus";
import { SearchMenu } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu";
import { SkipToContentButton } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SkipToContentButton";
import { SubjectItems } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SubjectItems";
import { SupportCenter } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SupportCenter";
import { UserProfileMenu } from "@/base/NewselaGlobalLayoutV2/NavigationBar/UserProfileMenu";
import TrialModal from "@/components/TrialModal";
import {
  useNavBarItemsVisibilityControl,
  NavBarItemVisibilityProvider,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/hooks/useNavBarItemsVisibilityControl";
import { SubjectsMenu } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SubjectsMenu";

import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import AISearchMenuContainer from "@/components/AIComponents/AISearchMenuContainer";
import { TrialButtons } from "./PremiumTrial/TrialButtons";

const NavigationBar = () => {
  const {
    trialModal: { trialModalProps },
  } = useNavigationBarV2Context();
  const {
    navBarRef,
    navBarLeftContainerRef,
    navBarRightContainerRef,
    isInitializing,
    isVisible,
  } = useNavBarItemsVisibilityControl();

  const { canSeeAiFeatures, hasPremiumLicense } = useGetUserProperties();
  const [aiSmartSearchExperiment] =
    useGetUserExperimentSegment("ai_assisted_search");
  const isAiSmartSearchExperimentOn = aiSmartSearchExperiment === "on";
  const canSeeAiSmartSearch = canSeeAiFeatures && isAiSmartSearchExperimentOn;

  const [isAISearchEnabled, setIsAISearchEnabled] = useState(false);
  const [shouldShowAISearch, setShouldShowAISearch] = useState(false);
  const SEARCH_LUNA_KEY = "isSearchWithLunaEnabled";

  useEffect(() => {
    const isSearchWithLunaEnabled = localStorage.getItem(SEARCH_LUNA_KEY);
    if (isSearchWithLunaEnabled !== null) {
      const parsed = isSearchWithLunaEnabled === "true";
      setIsAISearchEnabled(parsed);
      setShouldShowAISearch(parsed);
    } else {
      if (hasPremiumLicense) {
        setIsAISearchEnabled(true);
        setShouldShowAISearch(true);
      }
    }
  }, [hasPremiumLicense]);

  const handleAISearchToggle = (value: boolean) => {
    setIsAISearchEnabled(value);
    localStorage.setItem(SEARCH_LUNA_KEY, String(value));

    setTimeout(() => {
      setShouldShowAISearch(value);
    }, 300);
  };

  return (
    <header>
      <nav css={navigationBarContainerCss} ref={navBarRef}>
        <NavBarItemVisibilityProvider
          value={{
            isVisible,
          }}
        >
          <ul css={navigationBarLeftContainerCss} ref={navBarLeftContainerRef}>
            <AppSwitcherMenu />
            <NewselaLogo />
            {canSeeAiSmartSearch && shouldShowAISearch ? (
              <AISearchMenuContainer
                onToggle={handleAISearchToggle}
                isAISearchEnabled={isAISearchEnabled}
              />
            ) : (
              <SearchMenu
                onToggle={handleAISearchToggle}
                isAISearchEnabled={isAISearchEnabled}
              />
            )}
            <SkipToContentButton />
          </ul>

          <ul
            css={getNavigationBarRightContainerCss(isInitializing)}
            ref={navBarRightContainerRef}
          >
            <SubjectItems />
            <SubjectsMenu />
            <ResourceMenus />
            <HomeMenu />
            <TrialButtons
              rootCss={getTrialButtonsVisibilityCss(
                isVisible("trial-buttons-item"),
              )}
            />
            <UserProfileMenu />
            <SupportCenter />
          </ul>
        </NavBarItemVisibilityProvider>
      </nav>

      <TrialModal {...trialModalProps} />
    </header>
  );
};

NavigationBar.displayName = "NavigationBar";
export { NavigationBar };
